import type { JSONContent } from "@tiptap/core";
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { createStoryImageSignedUrl } from "@/features/story/api/storyStorage";
import {
  loadContinuationUsage as loadContinuationUsageClient,
  requestChapterPolish as requestChapterPolishClient,
  requestContinuationSuggestion as requestContinuationSuggestionClient,
  requestContinuityReview as requestContinuityReviewClient,
  requestSelectionEnhance as requestSelectionEnhanceClient,
  requestStructuralAnalysis as requestStructuralAnalysisClient,
  updateStructuralFindingStatus as updateStructuralFindingStatusClient,
} from "@/features/ai/aiClient";
import {
  AiActionError,
  getAiActionErrorMessageKey,
  getAiActionPrompt,
} from "@/features/ai/aiErrors";
import {
  mapAiGuardOutcome,
  mapStructuralAnalysisHistoryEntries,
} from "@/features/ai/aiMappers";
import type {
  AiAccessPromptView,
  AiGuardOutcomeView,
  ContinuationSuggestionMutationResult,
  ContinuationSuggestionSessionView,
  ContinuationUsageView,
  ContinuityIssueResolutionStatus,
  ContinuityIssueView,
  ContinuityReviewView,
  DiffSegment,
  EnhanceSourceContext,
  PolishSourceContext,
  StructuralAnalysisCalloutReviewState,
  StructuralAnalysisRunView,
  StructuralAnalysisStatusMutationResult,
  TextEnhancementStatus,
} from "@/features/ai/ai.types";
import {
  addSideStoryImage as addSideStoryImageClient,
  assignChapterRole as assignChapterRoleClient,
  createChapter as createChapterClient,
  createMainStory as createMainStoryClient,
  createSideStory as createSideStoryClient,
  deleteContinuityIssue as deleteContinuityIssueClient,
  deleteContinuationSuggestion as deleteContinuationSuggestionClient,
  deleteStructuralAnalysisRun as deleteStructuralAnalysisRunClient,
  resolveSideStoryView,
  formatStoryErrorMessage,
  getProjectStoryCapacity,
  loadChapterContinuityIssues as loadChapterContinuityIssuesClient,
  loadChapterContinuationSuggestions as loadChapterContinuationSuggestionsClient,
  loadStructuralAnalysisRuns as loadStructuralAnalysisRunsClient,
  loadStoryWorkspace,
  removeChapterImage as removeChapterImageClient,
  removeSideStoryImage as removeSideStoryImageClient,
  reorderStoryChapters as reorderStoryChaptersClient,
  replaceChapterImage as replaceChapterImageClient,
  StoryActionError,
  updateContinuityIssueResolution as updateContinuityIssueResolutionClient,
  updateChapter as updateChapterClient,
  updateMainStory as updateMainStoryClient,
  updateSideStory as updateSideStoryClient,
  deleteSideStory as deleteSideStoryClient,
} from "@/features/story/api/storyClient";
import { exportStoryPiece as exportStoryPieceClient } from "@/features/story/api/storyExportClient";
import {
  mapChapterRecordToView,
  mapStoryAttachment,
  mapMainStoryRecordToView,
} from "@/features/story/mappers/storyMappers";
import {
  applyChapterEdgeRoles,
  applyExclusiveChapterRole,
} from "@/features/story/utils/chapterUtils";
import { subscriptionStore } from "@/stores/subscriptionStore";
import {
  cloneEmptyStoryDocument,
  createSideStoryDraft,
  type ChapterView,
  type CreateSideStoryInput,
  type MainStoryView,
  type StoryExportRequestInput,
  type StoryExportResult,
  type SideStoryView,
  type StoryCapacitySummary,
  type StoryLimitPrompt,
  type StoryMode,
  type StoryWorkspaceView,
  type StructuralAnalysisScopeSelection,
  type UpdateChapterInput,
  type UpdateSideStoryInput,
} from "@/types/story";

export interface StoryWorkspaceDependencies {
  addSideStoryImage: typeof addSideStoryImageClient;
  assignChapterRole: typeof assignChapterRoleClient;
  createChapter: typeof createChapterClient;
  createMainStory: typeof createMainStoryClient;
  createSideStory: typeof createSideStoryClient;
  deleteContinuityIssue: typeof deleteContinuityIssueClient;
  deleteContinuationSuggestion: typeof deleteContinuationSuggestionClient;
  deleteStructuralAnalysisRun: typeof deleteStructuralAnalysisRunClient;
  deleteSideStory: typeof deleteSideStoryClient;
  exportStoryPiece: typeof exportStoryPieceClient;
  getProjectStoryCapacity: typeof getProjectStoryCapacity;
  loadContinuityIssues: typeof loadChapterContinuityIssuesClient;
  loadContinuationSuggestions: typeof loadChapterContinuationSuggestionsClient;
  loadContinuationUsage: typeof loadContinuationUsageClient;
  loadStructuralAnalysisRuns: typeof loadStructuralAnalysisRunsClient;
  loadStoryWorkspace: typeof loadStoryWorkspace;
  removeChapterImage: typeof removeChapterImageClient;
  removeSideStoryImage: typeof removeSideStoryImageClient;
  reorderStoryChapters: typeof reorderStoryChaptersClient;
  replaceChapterImage: typeof replaceChapterImageClient;
  requestChapterPolish: typeof requestChapterPolishClient;
  requestContinuationSuggestion: typeof requestContinuationSuggestionClient;
  requestContinuityReview: typeof requestContinuityReviewClient;
  requestSelectionEnhance: typeof requestSelectionEnhanceClient;
  requestStructuralAnalysis: typeof requestStructuralAnalysisClient;
  updateContinuityIssueResolution: typeof updateContinuityIssueResolutionClient;
  updateStructuralFindingStatus: typeof updateStructuralFindingStatusClient;
  updateChapter: typeof updateChapterClient;
  updateMainStory: typeof updateMainStoryClient;
  updateSideStory: typeof updateSideStoryClient;
}

export interface StoryWorkspaceNotice {
  kind: "error" | "success";
  message: string;
}

export interface EnhanceState {
  creditsRemaining: number | null;
  error: string | null;
  noImprovements: boolean;
  originalText: string | null;
  prompt: AiAccessPromptView | null;
  selectionFrom: number | null;
  selectionTo: number | null;
  sourceContext: EnhanceSourceContext | null;
  status: TextEnhancementStatus;
  suggestion: string | null;
}

export interface PolishState {
  creditsRemaining: number | null;
  diff: DiffSegment[] | null;
  error: string | null;
  noImprovements: boolean;
  originalText: string | null;
  prompt: AiAccessPromptView | null;
  sourceContext: PolishSourceContext | null;
  status: TextEnhancementStatus;
  suggestion: string | null;
}

export interface StoryWorkspaceStoreState {
  activeChapterId: string | null;
  activeSideStoryId: string | null;
  applyGeneratedChapterImage: (
    chapterId: string,
    imagePath: string,
    creditsRemaining?: number | null,
  ) => Promise<void>;
  applyGeneratedSideStoryImage: (
    storyId: string,
    imagePath: string,
    creditsRemaining?: number | null,
  ) => Promise<void>;
  capacity: StoryCapacitySummary | null;
  clearChapterAi: () => void;
  clearLimitPrompt: () => void;
  clearNotice: () => void;
  continuityDeleteBusyId: string | null;
  continuityError: string | null;
  continuityIssues: ContinuityIssueView[];
  continuityNoCanonContext: boolean;
  continuityGuardOutcome: AiGuardOutcomeView | null;
  continuityPrompt: AiAccessPromptView | null;
  continuityRequestStatus: "error" | "idle" | "loading" | "ready";
  continuityResolutionBusyId: string | null;
  continuityRetrievedCanonCount: number;
  continuityReviewSummary: string | null;
  continuityStatus: "error" | "idle" | "loading" | "ready";
  continuationDeleteBusyId: string | null;
  continuationError: string | null;
  continuationGuardOutcome: AiGuardOutcomeView | null;
  continuationPrompt: AiAccessPromptView | null;
  continuationRequestStatus: "error" | "idle" | "loading" | "ready";
  continuationStatus: "error" | "idle" | "loading" | "ready";
  continuationSuggestions: ContinuationSuggestionSessionView[];
  continuationUsage: ContinuationUsageView | null;
  currentAiCreditBalance: number | null;
  deleteStructuralAnalysisRun: (runId: string) => Promise<void>;
  dismissEnhanceSuggestion: () => void;
  dismissPolishSuggestion: () => void;
  enhanceState: EnhanceState;
  createChapter: () => Promise<ChapterView>;
  createMainStory: (mode: StoryMode) => Promise<MainStoryView>;
  createSideStory: (input: CreateSideStoryInput) => Promise<SideStoryView>;
  deleteContinuityIssue: (issueId: string) => Promise<void>;
  deleteContinuationSuggestion: (suggestionSetId: string) => Promise<void>;
  deleteSideStory: (storyId: string) => Promise<void>;
  exportStoryPiece: (
    input: StoryExportRequestInput,
  ) => Promise<StoryExportResult>;
  hydrateChapterAi: () => Promise<void>;
  hydrateWorkspace: (projectId: string) => Promise<void>;
  isCreatingMainStory: boolean;
  isCreatingSideStory: boolean;
  isRecoveringMedia: boolean;
  limitPrompt: StoryLimitPrompt | null;
  mainStory: MainStoryView | null;
  notice: StoryWorkspaceNotice | null;
  openChapter: (chapterId: string) => void;
  openMainStory: () => void;
  openSideStory: (storyId: string) => void;
  projectId: string | null;
  refreshMediaUrls: () => Promise<void>;
  removeChapterImage: (chapterId: string) => Promise<void>;
  removeSideStoryImage: (storyId: string, imagePath: string) => Promise<void>;
  reorderChapters: (orderedChapterIds: string[]) => Promise<void>;
  replaceChapterImage: (chapterId: string, file: File) => Promise<void>;
  requestContinuationSuggestions: () => Promise<ContinuationSuggestionMutationResult>;
  requestContinuityReview: () => Promise<ContinuityReviewView>;
  requestChapterPolish: (input: {
    chapterId: string;
    projectId: string;
  }) => Promise<void>;
  requestSelectionEnhance: (input: {
    chapterId: string;
    chapterTitle: string;
    paragraphContext: string;
    projectId: string;
    selectedText: string;
    selectionFrom: number;
    selectionTo: number;
  }) => Promise<void>;
  requestStructuralAnalysis: (
    scope: StructuralAnalysisScopeSelection,
  ) => Promise<StructuralAnalysisRunView>;
  saveChapter: (
    chapterId: string,
    input: UpdateChapterInput,
  ) => Promise<ChapterView>;
  saveMainStory: (content: JSONContent) => Promise<MainStoryView>;
  saveSideStory: (
    storyId: string,
    input: UpdateSideStoryInput,
  ) => Promise<SideStoryView>;
  selectContinuityIssue: (issueId: string | null) => void;
  selectedContinuityIssueId: string | null;
  sideStories: SideStoryView[];
  polishState: PolishState;
  structuralDeleteBusyId: string | null;
  structuralError: string | null;
  structuralGuardOutcome: AiGuardOutcomeView | null;
  structuralPrompt: AiAccessPromptView | null;
  structuralRequestStatus: "error" | "idle" | "loading" | "ready";
  structuralRuns: StructuralAnalysisRunView[];
  structuralStatus: "error" | "idle" | "loading" | "ready";
  structuralStatusBusyId: string | null;
  selectedStructuralRunId: string | null;
  startCreateSideStory: () => void;
  status: "idle" | "loading" | "ready" | "error";
  tier: StoryWorkspaceView["tier"] | null;
  selectStructuralAnalysisRun: (runId: string | null) => void;
  updateContinuityIssueResolution: (
    issueId: string,
    resolutionStatus: ContinuityIssueResolutionStatus,
  ) => Promise<ContinuityIssueView>;
  updateStructuralFindingStatus: (
    runId: string,
    dimensionName: StructuralAnalysisRunView["sections"][number]["dimension"],
    findingIndex: number,
    status: StructuralAnalysisCalloutReviewState,
  ) => Promise<StructuralAnalysisStatusMutationResult>;
  updateChapterRole: (
    chapterId: string,
    role: "opening" | "closing",
  ) => Promise<void>;
  uploadSideStoryImage: (storyId: string, file: File) => Promise<void>;
  resetEnhanceState: () => void;
  resetPolishState: () => void;
}

const defaultDependencies: StoryWorkspaceDependencies = {
  addSideStoryImage: addSideStoryImageClient,
  assignChapterRole: assignChapterRoleClient,
  createChapter: createChapterClient,
  createMainStory: createMainStoryClient,
  createSideStory: createSideStoryClient,
  deleteContinuityIssue: deleteContinuityIssueClient,
  deleteContinuationSuggestion: deleteContinuationSuggestionClient,
  deleteStructuralAnalysisRun: deleteStructuralAnalysisRunClient,
  deleteSideStory: deleteSideStoryClient,
  exportStoryPiece: exportStoryPieceClient,
  getProjectStoryCapacity,
  loadContinuityIssues: loadChapterContinuityIssuesClient,
  loadContinuationSuggestions: loadChapterContinuationSuggestionsClient,
  loadContinuationUsage: loadContinuationUsageClient,
  loadStructuralAnalysisRuns: loadStructuralAnalysisRunsClient,
  loadStoryWorkspace,
  removeChapterImage: removeChapterImageClient,
  removeSideStoryImage: removeSideStoryImageClient,
  reorderStoryChapters: reorderStoryChaptersClient,
  replaceChapterImage: replaceChapterImageClient,
  requestChapterPolish: requestChapterPolishClient,
  requestContinuationSuggestion: requestContinuationSuggestionClient,
  requestContinuityReview: requestContinuityReviewClient,
  requestSelectionEnhance: requestSelectionEnhanceClient,
  requestStructuralAnalysis: requestStructuralAnalysisClient,
  updateContinuityIssueResolution: updateContinuityIssueResolutionClient,
  updateStructuralFindingStatus: updateStructuralFindingStatusClient,
  updateChapter: updateChapterClient,
  updateMainStory: updateMainStoryClient,
  updateSideStory: updateSideStoryClient,
};

function getLimitPrompt(error: unknown) {
  return error instanceof StoryActionError ? (error.limitPrompt ?? null) : null;
}

function getAiMessage(
  feature: "continuation" | "continuity" | "enhance" | "polish" | "structural",
  error: unknown,
) {
  return error instanceof AiActionError
    ? getAiActionErrorMessageKey(feature, error)
    : formatStoryErrorMessage(error);
}

function getAiGuardOutcomeFromError(
  feature: "continuation" | "continuity" | "enhance" | "polish" | "structural",
  error: unknown,
) {
  if (!(error instanceof AiActionError)) {
    return null;
  }

  const guardCode = error.code === "rate-limit-reached"
    ? "AI_RATE_LIMIT_REACHED"
    : error.code === "budget-restricted"
      ? "AI_BUDGET_RESTRICTED"
      : error.code === "service-busy"
        ? "AI_SERVICE_BUSY"
        : undefined;

  return mapAiGuardOutcome({
    cacheStatus: error.cacheStatus,
    code: guardCode,
    creditsSafe: error.creditsSafe,
    feature,
    resetAt: error.resetAt,
    retryAfterSeconds: error.retryAfterSeconds,
    truncated: error.truncated,
  });
}

function createInitialEnhanceState(): EnhanceState {
  return {
    creditsRemaining: null,
    error: null,
    noImprovements: false,
    originalText: null,
    prompt: null,
    selectionFrom: null,
    selectionTo: null,
    sourceContext: null,
    status: "idle",
    suggestion: null,
  };
}

function createInitialPolishState(): PolishState {
  return {
    creditsRemaining: null,
    diff: null,
    error: null,
    noImprovements: false,
    originalText: null,
    prompt: null,
    sourceContext: null,
    status: "idle",
    suggestion: null,
  };
}

function getFallbackMainStoryTitle() {
  return "Main Story";
}

function applySelection(
  workspace: StoryWorkspaceView,
  selection: Pick<
    StoryWorkspaceStoreState,
    "activeChapterId" | "activeSideStoryId"
  >,
) {
  const nextActiveSideStoryId =
    selection.activeSideStoryId &&
    workspace.sideStories.some(
      (story) => story.id === selection.activeSideStoryId,
    )
      ? selection.activeSideStoryId
      : null;

  if (nextActiveSideStoryId) {
    return {
      activeChapterId: null,
      activeSideStoryId: nextActiveSideStoryId,
    };
  }

  if (workspace.mainStory?.mode === "chapters") {
    const nextActiveChapterId =
      selection.activeChapterId &&
      workspace.mainStory.chapters.some(
        (chapter) => chapter.id === selection.activeChapterId,
      )
        ? selection.activeChapterId
        : (workspace.mainStory.chapters[0]?.id ?? null);

    return {
      activeChapterId: nextActiveChapterId,
      activeSideStoryId: null,
    };
  }

  return {
    activeChapterId: null,
    activeSideStoryId: null,
  };
}

function upsertSideStory(
  sideStories: SideStoryView[],
  nextStory: SideStoryView,
) {
  const previous = sideStories.find((story) => story.id === nextStory.id);
  const nextStories = previous
    ? sideStories.map((story) =>
        story.id === nextStory.id ? nextStory : story,
      )
    : [nextStory, ...sideStories];

  return [...nextStories].sort(
    (left, right) =>
      new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
  );
}

export function createStoryWorkspaceStore(
  deps: StoryWorkspaceDependencies = defaultDependencies,
) {
  return createStore<StoryWorkspaceStoreState>((set, get) => ({
    activeChapterId: null,
    activeSideStoryId: null,
    capacity: null,
    continuityDeleteBusyId: null,
    continuityError: null,
    continuityGuardOutcome: null,
    continuityIssues: [],
    continuityNoCanonContext: false,
    continuityPrompt: null,
    continuityRequestStatus: "idle",
    continuityResolutionBusyId: null,
    continuityRetrievedCanonCount: 0,
    continuityReviewSummary: null,
    continuityStatus: "idle",
    continuationDeleteBusyId: null,
    continuationError: null,
    continuationGuardOutcome: null,
    continuationPrompt: null,
    continuationRequestStatus: "idle",
    continuationStatus: "idle",
    continuationSuggestions: [],
    continuationUsage: null,
    currentAiCreditBalance: null,
    enhanceState: createInitialEnhanceState(),
    isCreatingMainStory: false,
    isCreatingSideStory: false,
    isRecoveringMedia: false,
    limitPrompt: null,
    mainStory: null,
    notice: null,
    projectId: null,
    sideStories: [],
    polishState: createInitialPolishState(),
    structuralDeleteBusyId: null,
    structuralError: null,
    structuralGuardOutcome: null,
    structuralPrompt: null,
    structuralRequestStatus: "idle",
    structuralRuns: [],
    structuralStatus: "idle",
    structuralStatusBusyId: null,
    selectedStructuralRunId: null,
    status: "idle",
    tier: null,
    selectedContinuityIssueId: null,

    resetEnhanceState: () => {
      set({
        enhanceState: createInitialEnhanceState(),
      });
    },

    resetPolishState: () => {
      set({
        polishState: createInitialPolishState(),
      });
    },

    dismissEnhanceSuggestion: () => {
      get().resetEnhanceState();
    },

    dismissPolishSuggestion: () => {
      get().resetPolishState();
    },

    applyGeneratedChapterImage: async (
      chapterId,
      imagePath,
      creditsRemaining = null,
    ) => {
      const imageUrl = await createStoryImageSignedUrl(imagePath).catch(
        () => null,
      );

      if (typeof creditsRemaining === "number") {
        subscriptionStore.getState().setCreditBalance(creditsRemaining);
      }

      set((state) => ({
        mainStory:
          state.mainStory?.mode === "chapters"
            ? {
                ...state.mainStory,
                chapters: state.mainStory.chapters.map((chapter) =>
                  chapter.id === chapterId
                    ? {
                        ...chapter,
                        hasHeaderImage: true,
                        headerImageUrl: imageUrl,
                        imagePath,
                        imageUrl,
                      }
                    : chapter,
                ),
              }
            : state.mainStory,
      }));
    },

    applyGeneratedSideStoryImage: async (
      storyId,
      imagePath,
      creditsRemaining = null,
    ) => {
      const imageUrl = await createStoryImageSignedUrl(imagePath).catch(
        () => null,
      );

      if (typeof creditsRemaining === "number") {
        subscriptionStore.getState().setCreditBalance(creditsRemaining);
      }

      set((state) => {
        const currentStory = state.sideStories.find(
          (story) => story.id === storyId,
        );
        if (!currentStory) {
          return {
            sideStories: state.sideStories,
          };
        }

        return {
          sideStories: upsertSideStory(state.sideStories, {
            ...currentStory,
            attachments: currentStory.attachments.some(
              (attachment) => attachment.path === imagePath,
            )
              ? currentStory.attachments
              : [
                  ...currentStory.attachments,
                  mapStoryAttachment(imagePath, imageUrl, currentStory.title),
                ],
            updatedAt: new Date().toISOString(),
          }),
        };
      });
    },

    clearChapterAi: () => {
      set({
        continuityDeleteBusyId: null,
        continuityError: null,
        continuityGuardOutcome: null,
        continuityIssues: [],
        continuityNoCanonContext: false,
        continuityPrompt: null,
        continuityRequestStatus: "idle",
        continuityResolutionBusyId: null,
        continuityRetrievedCanonCount: 0,
        continuityReviewSummary: null,
        continuityStatus: "idle",
        continuationDeleteBusyId: null,
        continuationError: null,
        continuationGuardOutcome: null,
        continuationPrompt: null,
        continuationRequestStatus: "idle",
        continuationStatus: "idle",
        continuationSuggestions: [],
        continuationUsage: null,
        currentAiCreditBalance: null,
        enhanceState: createInitialEnhanceState(),
        selectedContinuityIssueId: null,
        polishState: createInitialPolishState(),
        structuralDeleteBusyId: null,
        structuralError: null,
        structuralGuardOutcome: null,
        structuralPrompt: null,
        structuralRequestStatus: "idle",
        structuralRuns: [],
        structuralStatus: "idle",
        structuralStatusBusyId: null,
        selectedStructuralRunId: null,
      });
    },

    hydrateWorkspace: async (projectId) => {
      set({
        activeChapterId: null,
        activeSideStoryId: null,
        continuityDeleteBusyId: null,
        continuityError: null,
        continuityGuardOutcome: null,
        continuityIssues: [],
        continuityNoCanonContext: false,
        continuityPrompt: null,
        continuityRequestStatus: "idle",
        continuityResolutionBusyId: null,
        continuityRetrievedCanonCount: 0,
        continuityReviewSummary: null,
        continuityStatus: "idle",
        continuationDeleteBusyId: null,
        continuationError: null,
        continuationGuardOutcome: null,
        continuationPrompt: null,
        continuationRequestStatus: "idle",
        continuationStatus: "idle",
        continuationSuggestions: [],
        continuationUsage: null,
        currentAiCreditBalance: null,
        enhanceState: createInitialEnhanceState(),
        isCreatingSideStory: false,
        isRecoveringMedia: false,
        limitPrompt: null,
        mainStory: null,
        notice: null,
        projectId,
        selectedContinuityIssueId: null,
        polishState: createInitialPolishState(),
        sideStories: [],
        structuralDeleteBusyId: null,
        structuralError: null,
        structuralGuardOutcome: null,
        structuralPrompt: null,
        structuralRequestStatus: "idle",
        structuralRuns: [],
        structuralStatus: "idle",
        structuralStatusBusyId: null,
        selectedStructuralRunId: null,
        status: "loading",
      });

      try {
        const [workspace, capacity] = await Promise.all([
          deps.loadStoryWorkspace(projectId),
          deps.getProjectStoryCapacity(projectId),
        ]);

        set((state) => ({
          capacity,
          isCreatingSideStory: false,
          isRecoveringMedia: false,
          limitPrompt: null,
          mainStory: workspace.mainStory,
          projectId,
          sideStories: workspace.sideStories,
          status: "ready",
          tier: workspace.tier,
          ...applySelection(workspace, state),
        }));
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
          projectId,
          status: "error",
        });
      }
    },

    hydrateChapterAi: async () => {
      const activeChapterId = get().activeChapterId;
      const projectId = get().projectId;
      const tier = get().tier;
      const mainStory = get().mainStory;
      const activeChapter =
        mainStory?.mode === "chapters"
          ? (mainStory.chapters.find(
              (chapter) => chapter.id === activeChapterId,
            ) ?? null)
          : null;

      if (!activeChapterId || !projectId || !tier || !activeChapter) {
        get().clearChapterAi();
        return;
      }

      set({
        continuityDeleteBusyId: null,
        continuityError: null,
        continuityGuardOutcome: null,
        continuityIssues: [],
        continuityNoCanonContext: false,
        continuityPrompt: null,
        continuityRequestStatus: "idle",
        continuityResolutionBusyId: null,
        continuityRetrievedCanonCount: 0,
        continuityReviewSummary: null,
        continuityStatus: "loading",
        continuationDeleteBusyId: null,
        continuationError: null,
        continuationGuardOutcome: null,
        continuationPrompt: null,
        continuationRequestStatus: "idle",
        continuationStatus: "loading",
        continuationSuggestions: [],
        enhanceState: createInitialEnhanceState(),
        polishState: createInitialPolishState(),
        selectedContinuityIssueId: null,
        structuralDeleteBusyId: null,
        structuralError: null,
        structuralGuardOutcome: null,
        structuralPrompt: null,
        structuralRequestStatus: "idle",
        structuralRuns: [],
        structuralStatus: "loading",
        structuralStatusBusyId: null,
      });

      try {
        const storyChapters =
          mainStory?.mode === "chapters" ? mainStory.chapters : [];
        const previousSelectedStructuralRunId = get().selectedStructuralRunId;
        const [
          continuationSuggestions,
          continuationUsage,
          continuityIssues,
          structuralRuns,
        ] = await Promise.all([
          deps.loadContinuationSuggestions(activeChapterId),
          deps.loadContinuationUsage(tier),
          deps.loadContinuityIssues(projectId, activeChapterId),
          deps.loadStructuralAnalysisRuns(
            projectId,
            activeChapter.storyId,
            storyChapters,
          ),
        ]);

        set({
          continuityError: null,
          continuityGuardOutcome: null,
          continuityIssues,
          continuityNoCanonContext: false,
          continuityPrompt: null,
          continuityRequestStatus: "idle",
          continuityRetrievedCanonCount: 0,
          continuityReviewSummary: null,
          continuityStatus: "ready",
          continuationError: null,
          continuationGuardOutcome: null,
          continuationPrompt: null,
          continuationRequestStatus: "idle",
          continuationStatus: "ready",
          continuationSuggestions,
          continuationUsage,
          selectedStructuralRunId: structuralRuns.some(
            (run) => run.id === previousSelectedStructuralRunId,
          )
            ? previousSelectedStructuralRunId
            : (structuralRuns[0]?.id ?? null),
          selectedContinuityIssueId: continuityIssues[0]?.id ?? null,
          structuralDeleteBusyId: null,
          structuralError: null,
          structuralGuardOutcome: null,
          structuralPrompt: null,
          structuralRequestStatus: "idle",
          structuralRuns,
          structuralStatus: "ready",
          structuralStatusBusyId: null,
        });
      } catch (error) {
        set({
          continuityError: getAiMessage("continuity", error),
          continuityGuardOutcome: getAiGuardOutcomeFromError("continuity", error),
          continuityIssues: [],
          continuityNoCanonContext: false,
          continuityPrompt:
            error instanceof AiActionError ? getAiActionPrompt(error) : null,
          continuityRequestStatus: "error",
          continuityStatus: "error",
          continuationError: getAiMessage("continuation", error),
          continuationGuardOutcome: getAiGuardOutcomeFromError("continuation", error),
          continuationPrompt:
            error instanceof AiActionError ? getAiActionPrompt(error) : null,
          continuationRequestStatus: "error",
          continuationStatus: "error",
          continuationSuggestions: [],
          continuationUsage: null,
          enhanceState: {
            ...createInitialEnhanceState(),
            error: getAiMessage("enhance", error),
            prompt: error instanceof AiActionError ? getAiActionPrompt(error) : null,
            status: "error",
          },
          polishState: {
            ...createInitialPolishState(),
            error: getAiMessage("polish", error),
            prompt: error instanceof AiActionError ? getAiActionPrompt(error) : null,
            status: "error",
          },
          structuralDeleteBusyId: null,
          structuralError: getAiMessage("structural", error),
          structuralGuardOutcome: getAiGuardOutcomeFromError("structural", error),
          structuralPrompt:
            error instanceof AiActionError ? getAiActionPrompt(error) : null,
          structuralRequestStatus: "error",
          structuralRuns: [],
          structuralStatus: "error",
          structuralStatusBusyId: null,
        });
      }
    },

    createMainStory: async (mode) => {
      const projectId = get().projectId;
      if (!projectId) {
        throw new StoryActionError("not-found", "That project is unavailable.");
      }

      set({
        isCreatingMainStory: true,
        notice: null,
      });

      try {
        const [record, capacity] = await Promise.all([
          deps.createMainStory(projectId, {
            body: cloneEmptyStoryDocument(),
            mode,
            summary: "",
            title: getFallbackMainStoryTitle(),
          }),
          deps.getProjectStoryCapacity(projectId).catch(() => get().capacity),
        ]);

        const mainStory = mapMainStoryRecordToView(record, []);

        set({
          activeChapterId: null,
          activeSideStoryId: null,
          capacity,
          isCreatingMainStory: false,
          isCreatingSideStory: false,
          limitPrompt: null,
          mainStory,
          notice: null,
          status: "ready",
          tier: capacity?.tier ?? get().tier ?? "free",
        });

        return mainStory;
      } catch (error) {
        set({
          isCreatingMainStory: false,
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    saveMainStory: async (content) => {
      const mainStory = get().mainStory;
      if (!mainStory) {
        throw new StoryActionError(
          "not-found",
          "The Main Story is unavailable.",
        );
      }

      try {
        const updated = await deps.updateMainStory(mainStory.id, {
          body: content,
          summary: mainStory.summary,
        });
        const nextMainStory = mapMainStoryRecordToView(
          updated,
          mainStory.chapters,
        );

        set({
          mainStory: nextMainStory,
          notice: null,
        });

        return nextMainStory;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    openMainStory: () => {
      const mainStory = get().mainStory;
      set({
        activeChapterId:
          mainStory?.mode === "chapters" ? get().activeChapterId : null,
        activeSideStoryId: null,
        enhanceState: createInitialEnhanceState(),
        isCreatingSideStory: false,
        polishState: createInitialPolishState(),
      });
    },

    openChapter: (chapterId) => {
      set({
        activeChapterId: chapterId,
        activeSideStoryId: null,
        enhanceState: createInitialEnhanceState(),
        isCreatingSideStory: false,
        polishState: createInitialPolishState(),
      });
    },

    createChapter: async () => {
      const projectId = get().projectId;
      const mainStory = get().mainStory;

      if (!projectId || !mainStory || mainStory.mode !== "chapters") {
        throw new StoryActionError(
          "not-found",
          "That chapter workspace is unavailable.",
        );
      }

      try {
        const created = await deps.createChapter(mainStory.id, {
          body: cloneEmptyStoryDocument(),
          status: "draft",
          title: `Chapter ${mainStory.chapters.length + 1}`,
        });

        const [workspace, capacity] = await Promise.all([
          deps.loadStoryWorkspace(projectId),
          deps.getProjectStoryCapacity(projectId).catch(() => get().capacity),
        ]);

        const createdChapter = workspace.mainStory?.chapters.find(
          (chapter) => chapter.id === created.id,
        );
        if (!workspace.mainStory || !createdChapter) {
          throw new StoryActionError(
            "not-found",
            "The created chapter is unavailable.",
          );
        }

        set({
          activeChapterId: created.id,
          activeSideStoryId: null,
          capacity,
          limitPrompt: null,
          mainStory: workspace.mainStory,
          notice: null,
          sideStories: workspace.sideStories,
          tier: workspace.tier,
        });

        return createdChapter;
      } catch (error) {
        set({
          limitPrompt: getLimitPrompt(error),
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    saveChapter: async (chapterId, input) => {
      const mainStory = get().mainStory;
      if (!mainStory || mainStory.mode !== "chapters") {
        throw new StoryActionError("not-found", "That chapter is unavailable.");
      }

      try {
        const updatedRecord = await deps.updateChapter(chapterId, input);
        const currentChapter = mainStory.chapters.find(
          (chapter) => chapter.id === chapterId,
        );
        if (!currentChapter) {
          throw new StoryActionError(
            "not-found",
            "That chapter is unavailable.",
          );
        }

        const nextChapter = mapChapterRecordToView(
          updatedRecord,
          currentChapter.imageUrl,
        );

        set({
          activeChapterId: chapterId,
          mainStory: {
            ...mainStory,
            chapters: mainStory.chapters.map((chapter) =>
              chapter.id === chapterId ? nextChapter : chapter,
            ),
          },
          notice: null,
        });

        return nextChapter;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    requestSelectionEnhance: async (input) => {
      set({
        enhanceState: {
          ...createInitialEnhanceState(),
          originalText: input.selectedText,
          selectionFrom: input.selectionFrom,
          selectionTo: input.selectionTo,
          status: "loading",
        },
      });

      try {
        const result = await deps.requestSelectionEnhance({
          chapterId: input.chapterId,
          chapterTitle: input.chapterTitle,
          paragraphContext: input.paragraphContext,
          projectId: input.projectId,
          selectedText: input.selectedText,
        });

        set((state) => ({
          currentAiCreditBalance:
            typeof result.creditsRemaining === "number"
              ? result.creditsRemaining
              : state.currentAiCreditBalance,
          enhanceState: {
            creditsRemaining: result.creditsRemaining,
            error: null,
            noImprovements: result.noImprovements,
            originalText: input.selectedText,
            prompt: null,
            selectionFrom: input.selectionFrom,
            selectionTo: input.selectionTo,
            sourceContext: result.sourceContext,
            status: "suggestion_ready",
            suggestion: result.suggestion,
          },
        }));
      } catch (error) {
        set((state) => ({
          currentAiCreditBalance:
            error instanceof AiActionError &&
            typeof error.currentBalance === "number"
              ? error.currentBalance
              : state.currentAiCreditBalance,
          enhanceState: {
            ...state.enhanceState,
            creditsRemaining:
              error instanceof AiActionError &&
              typeof error.currentBalance === "number"
                ? error.currentBalance
                : state.enhanceState.creditsRemaining,
            error: getAiMessage("enhance", error),
            prompt: error instanceof AiActionError ? getAiActionPrompt(error) : null,
            status: "error",
          },
        }));
        throw error;
      }
    },

    requestChapterPolish: async (input) => {
      if (get().polishState.status === "loading") {
        return;
      }

      set({
        polishState: {
          ...createInitialPolishState(),
          status: "loading",
        },
      });

      try {
        const result = await deps.requestChapterPolish(input);

        set((state) => ({
          currentAiCreditBalance:
            typeof result.creditsRemaining === "number"
              ? result.creditsRemaining
              : state.currentAiCreditBalance,
          polishState: {
            creditsRemaining: result.creditsRemaining,
            diff: result.diff,
            error: null,
            noImprovements: result.noImprovements,
            originalText: result.originalText,
            prompt: null,
            sourceContext: result.sourceContext,
            status: "suggestion_ready",
            suggestion: result.suggestion,
          },
        }));
      } catch (error) {
        set((state) => ({
          currentAiCreditBalance:
            error instanceof AiActionError &&
            typeof error.currentBalance === "number"
              ? error.currentBalance
              : state.currentAiCreditBalance,
          polishState: {
            ...state.polishState,
            creditsRemaining:
              error instanceof AiActionError &&
              typeof error.currentBalance === "number"
                ? error.currentBalance
                : state.polishState.creditsRemaining,
            error: getAiMessage("polish", error),
            prompt: error instanceof AiActionError ? getAiActionPrompt(error) : null,
            status: "error",
          },
        }));
        throw error;
      }
    },

    requestContinuationSuggestions: async () => {
      const activeChapterId = get().activeChapterId;
      const projectId = get().projectId;

      if (!activeChapterId || !projectId) {
        throw new StoryActionError("not-found", "That chapter is unavailable.");
      }

      set({
        continuationDeleteBusyId: null,
        continuationError: null,
        continuationGuardOutcome: null,
        continuationPrompt: null,
        continuationRequestStatus: "loading",
      });

      try {
        const result = await deps.requestContinuationSuggestion(
          projectId,
          activeChapterId,
        );

        set((state) => ({
          continuationDeleteBusyId: null,
          continuationError: null,
          continuationGuardOutcome: result.guardOutcome,
          continuationPrompt: null,
          continuationRequestStatus: "ready",
          continuationSuggestions: [
            result.session,
            ...state.continuationSuggestions,
          ],
          continuationUsage: result.usage,
          currentAiCreditBalance:
            typeof result.usage.creditsRemaining === "number"
              ? result.usage.creditsRemaining
              : state.currentAiCreditBalance,
        }));

        return result;
      } catch (error) {
        const aiPrompt =
          error instanceof AiActionError ? getAiActionPrompt(error) : null;
        set({
          continuationError: getAiMessage("continuation", error),
          continuationGuardOutcome: getAiGuardOutcomeFromError("continuation", error),
          continuationPrompt: aiPrompt,
          continuationRequestStatus: "error",
          currentAiCreditBalance:
            error instanceof AiActionError &&
            typeof error.currentBalance === "number"
              ? error.currentBalance
              : get().currentAiCreditBalance,
          continuationUsage:
            error instanceof AiActionError &&
            typeof error.usesRemainingToday === "number"
              ? {
                  creditCost: 0,
                  creditsRemaining: null,
                  tier: "free",
                  usesRemainingToday: error.usesRemainingToday,
                }
              : get().continuationUsage,
        });
        throw error;
      }
    },

    deleteContinuationSuggestion: async (suggestionSetId) => {
      set({
        continuationDeleteBusyId: suggestionSetId,
        continuationError: null,
        continuationPrompt: null,
      });

      try {
        await deps.deleteContinuationSuggestion(suggestionSetId);

        set((state) => ({
          continuationDeleteBusyId: null,
          continuationSuggestions: state.continuationSuggestions.filter(
            (session) => session.id !== suggestionSetId,
          ),
        }));
      } catch (error) {
        set({
          continuationDeleteBusyId: null,
          continuationError: getAiMessage("continuation", error),
        });
        throw error;
      }
    },

    requestContinuityReview: async () => {
      const activeChapterId = get().activeChapterId;
      const projectId = get().projectId;

      if (!activeChapterId || !projectId) {
        throw new StoryActionError("not-found", "That chapter is unavailable.");
      }

      set({
        continuityDeleteBusyId: null,
        continuityError: null,
        continuityGuardOutcome: null,
        continuityPrompt: null,
        continuityRequestStatus: "loading",
        continuityResolutionBusyId: null,
      });

      try {
        const result = await deps.requestContinuityReview(
          projectId,
          activeChapterId,
        );

        set({
          continuityDeleteBusyId: null,
          continuityError: null,
          continuityGuardOutcome: result.guardOutcome,
          continuityIssues: result.issues,
          continuityNoCanonContext: result.noCanonContext,
          continuityPrompt: null,
          continuityRequestStatus: "ready",
          continuityRetrievedCanonCount: result.retrievedCanonCount,
          continuityReviewSummary: result.reviewSummary,
          continuityStatus: "ready",
          currentAiCreditBalance:
            typeof result.creditsRemaining === "number"
              ? result.creditsRemaining
              : get().currentAiCreditBalance,
          selectedContinuityIssueId: result.issues[0]?.id ?? null,
        });

        return result;
      } catch (error) {
        set({
          continuityError: getAiMessage("continuity", error),
          continuityGuardOutcome: getAiGuardOutcomeFromError("continuity", error),
          continuityPrompt:
            error instanceof AiActionError ? getAiActionPrompt(error) : null,
          continuityRequestStatus: "error",
          currentAiCreditBalance:
            error instanceof AiActionError &&
            typeof error.currentBalance === "number"
              ? error.currentBalance
              : get().currentAiCreditBalance,
        });
        throw error;
      }
    },

    updateContinuityIssueResolution: async (issueId, resolutionStatus) => {
      const projectId = get().projectId;
      if (!projectId) {
        throw new StoryActionError("not-found", "That chapter is unavailable.");
      }

      set({
        continuityError: null,
        continuityResolutionBusyId: issueId,
      });

      try {
        const nextIssue = await deps.updateContinuityIssueResolution(
          projectId,
          issueId,
          resolutionStatus,
        );

        set((state) => ({
          continuityError: null,
          continuityIssues: state.continuityIssues.map((issue) =>
            issue.id === issueId ? nextIssue : issue,
          ),
          continuityResolutionBusyId: null,
        }));

        return nextIssue;
      } catch (error) {
        set({
          continuityError: getAiMessage("continuity", error),
          continuityResolutionBusyId: null,
        });
        throw error;
      }
    },

    deleteContinuityIssue: async (issueId) => {
      set({
        continuityDeleteBusyId: issueId,
        continuityError: null,
      });

      try {
        await deps.deleteContinuityIssue(issueId);

        set((state) => {
          const nextIssues = state.continuityIssues.filter(
            (issue) => issue.id !== issueId,
          );

          return {
            continuityDeleteBusyId: null,
            continuityIssues: nextIssues,
            selectedContinuityIssueId:
              state.selectedContinuityIssueId === issueId
                ? (nextIssues[0]?.id ?? null)
                : state.selectedContinuityIssueId,
          };
        });
      } catch (error) {
        set({
          continuityDeleteBusyId: null,
          continuityError: getAiMessage("continuity", error),
        });
        throw error;
      }
    },

    selectContinuityIssue: (issueId) => {
      set({
        selectedContinuityIssueId: issueId,
      });
    },

    requestStructuralAnalysis: async (scope) => {
      const projectId = get().projectId;
      const mainStory = get().mainStory;

      if (!projectId || !mainStory || mainStory.mode !== "chapters") {
        throw new StoryActionError(
          "not-found",
          "That chapter workspace is unavailable.",
        );
      }

      set({
        structuralDeleteBusyId: null,
        structuralError: null,
        structuralGuardOutcome: null,
        structuralPrompt: null,
        structuralRequestStatus: "loading",
      });

      try {
        const result = await deps.requestStructuralAnalysis(
          projectId,
          mainStory.id,
          scope,
          mainStory.chapters,
        );

        set((state) => ({
          currentAiCreditBalance:
            typeof result.creditsRemaining === "number"
              ? result.creditsRemaining
              : state.currentAiCreditBalance,
          selectedStructuralRunId: result.run.id,
          structuralDeleteBusyId: null,
          structuralError: null,
          structuralGuardOutcome: result.guardOutcome,
          structuralPrompt: null,
          structuralRequestStatus: "ready",
          structuralRuns: [
            result.run,
            ...state.structuralRuns.filter((run) => run.id !== result.run.id),
          ],
          structuralStatus: "ready",
          structuralStatusBusyId: null,
        }));

        return result.run;
      } catch (error) {
        set({
          currentAiCreditBalance:
            error instanceof AiActionError &&
            typeof error.currentBalance === "number"
              ? error.currentBalance
              : get().currentAiCreditBalance,
          structuralError: getAiMessage("structural", error),
          structuralGuardOutcome: getAiGuardOutcomeFromError("structural", error),
          structuralPrompt:
            error instanceof AiActionError ? getAiActionPrompt(error) : null,
          structuralRequestStatus: "error",
        });
        throw error;
      }
    },

    selectStructuralAnalysisRun: (runId) => {
      set({
        selectedStructuralRunId: runId,
      });
    },

    updateStructuralFindingStatus: async (
      runId,
      dimensionName,
      findingIndex,
      status,
    ) => {
      set({
        structuralError: null,
        structuralStatusBusyId: `${runId}:${dimensionName}:${findingIndex}`,
      });

      try {
        const result = await deps.updateStructuralFindingStatus({
          dimensionName,
          findingIndex,
          runId,
          status,
        });

        set((state) => ({
          structuralRuns: state.structuralRuns.map((run) =>
            run.id !== runId
              ? run
              : {
                  ...run,
                  sections: run.sections.map((section) =>
                    section.dimension !== dimensionName
                      ? section
                      : {
                          ...section,
                          findings: section.findings.map((finding) =>
                            finding.findingIndex !== findingIndex
                              ? finding
                              : {
                                  ...finding,
                                  reviewState: result.status,
                                  reviewStateLabel:
                                    result.status === "acknowledged"
                                      ? "Acknowledged"
                                      : result.status === "dismissed"
                                        ? "Dismissed"
                                        : "Open",
                                },
                          ),
                        },
                  ),
                },
          ),
          structuralStatusBusyId: null,
        }));

        return result;
      } catch (error) {
        set({
          structuralError: getAiMessage("structural", error),
          structuralStatusBusyId: null,
        });
        throw error;
      }
    },

    deleteStructuralAnalysisRun: async (runId) => {
      set({
        structuralDeleteBusyId: runId,
        structuralError: null,
      });

      try {
        await deps.deleteStructuralAnalysisRun(runId);

        set((state) => {
          const structuralRuns = state.structuralRuns.filter(
            (run) => run.id !== runId,
          );

          return {
            selectedStructuralRunId:
              state.selectedStructuralRunId === runId
                ? (structuralRuns[0]?.id ?? null)
                : state.selectedStructuralRunId,
            structuralDeleteBusyId: null,
            structuralRuns,
          };
        });
      } catch (error) {
        set({
          structuralDeleteBusyId: null,
          structuralError: getAiMessage("structural", error),
        });
        throw error;
      }
    },

    reorderChapters: async (orderedChapterIds) => {
      const mainStory = get().mainStory;
      if (!mainStory || mainStory.mode !== "chapters") {
        throw new StoryActionError(
          "not-found",
          "That chapter list is unavailable.",
        );
      }

      const reordered = applyChapterEdgeRoles(
        orderedChapterIds
          .map((chapterId, index) => {
            const chapter = mainStory.chapters.find(
              (item) => item.id === chapterId,
            );
            return chapter ? { ...chapter, sortOrder: index } : null;
          })
          .filter((chapter): chapter is ChapterView => Boolean(chapter)),
      );

      set({
        mainStory: {
          ...mainStory,
          chapters: reordered,
        },
      });

      try {
        await deps.reorderStoryChapters(mainStory.id, orderedChapterIds);
      } catch (error) {
        const projectId = get().projectId;
        if (projectId) {
          await get().hydrateWorkspace(projectId);
        }
        throw error;
      }
    },

    updateChapterRole: async (chapterId, role) => {
      const projectId = get().projectId;
      if (!projectId) {
        throw new StoryActionError("not-found", "That chapter is unavailable.");
      }

      const mainStory = get().mainStory;
      if (mainStory?.mode === "chapters") {
        set({
          mainStory: {
            ...mainStory,
            chapters: applyExclusiveChapterRole(
              mainStory.chapters,
              chapterId,
              role,
            ),
          },
        });
      }

      try {
        await deps.assignChapterRole(chapterId, role);
        const workspace = await deps.loadStoryWorkspace(projectId);

        set({
          activeChapterId: chapterId,
          mainStory: workspace.mainStory,
          notice: null,
          sideStories: workspace.sideStories,
          tier: workspace.tier,
        });
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    replaceChapterImage: async (chapterId, file) => {
      const projectId = get().projectId;
      if (!projectId) {
        throw new StoryActionError("not-found", "That chapter is unavailable.");
      }

      try {
        await deps.replaceChapterImage(chapterId, file);
        const workspace = await deps.loadStoryWorkspace(projectId);

        set({
          activeChapterId: chapterId,
          mainStory: workspace.mainStory,
          notice: null,
          sideStories: workspace.sideStories,
          tier: workspace.tier,
        });
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    removeChapterImage: async (chapterId) => {
      const projectId = get().projectId;
      if (!projectId) {
        throw new StoryActionError("not-found", "That chapter is unavailable.");
      }

      try {
        await deps.removeChapterImage(chapterId);
        const workspace = await deps.loadStoryWorkspace(projectId);

        set({
          activeChapterId: chapterId,
          mainStory: workspace.mainStory,
          notice: null,
          sideStories: workspace.sideStories,
          tier: workspace.tier,
        });
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    startCreateSideStory: () => {
      set({
        activeChapterId: null,
        activeSideStoryId: null,
        enhanceState: createInitialEnhanceState(),
        isCreatingSideStory: true,
        limitPrompt: null,
        polishState: createInitialPolishState(),
      });
    },

    openSideStory: (storyId) => {
      set({
        activeChapterId: null,
        activeSideStoryId: storyId,
        enhanceState: createInitialEnhanceState(),
        isCreatingSideStory: false,
        polishState: createInitialPolishState(),
      });
    },

    createSideStory: async (input) => {
      const projectId = get().projectId;
      if (!projectId) {
        throw new StoryActionError("not-found", "That project is unavailable.");
      }

      try {
        const [record, capacity] = await Promise.all([
          deps.createSideStory(projectId, input),
          deps.getProjectStoryCapacity(projectId).catch(() => get().capacity),
        ]);

        const sideStory = await resolveSideStoryView(record, []);

        set((state) => ({
          activeChapterId: null,
          activeSideStoryId: sideStory.id,
          capacity,
          enhanceState: createInitialEnhanceState(),
          isCreatingSideStory: false,
          limitPrompt: null,
          notice: null,
          polishState: createInitialPolishState(),
          sideStories: upsertSideStory(state.sideStories, sideStory),
        }));

        return sideStory;
      } catch (error) {
        set({
          limitPrompt: getLimitPrompt(error),
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    saveSideStory: async (storyId, input) => {
      const currentStory = get().sideStories.find(
        (story) => story.id === storyId,
      );
      if (!currentStory) {
        throw new StoryActionError(
          "not-found",
          "That Side Story is unavailable.",
        );
      }

      try {
        const updatedRecord = await deps.updateSideStory(storyId, input);
        const sideStory = await resolveSideStoryView(
          updatedRecord,
          currentStory.attachments,
        );

        set((state) => ({
          activeSideStoryId: storyId,
          notice: null,
          sideStories: upsertSideStory(state.sideStories, sideStory),
        }));

        return sideStory;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    uploadSideStoryImage: async (storyId, file) => {
      const currentStory = get().sideStories.find(
        (story) => story.id === storyId,
      );
      if (!currentStory) {
        throw new StoryActionError(
          "not-found",
          "That Side Story is unavailable.",
        );
      }

      try {
        const updatedRecord = await deps.addSideStoryImage(storyId, file);
        const sideStory = await resolveSideStoryView(
          updatedRecord,
          currentStory.attachments,
        );

        set((state) => ({
          activeSideStoryId: storyId,
          isCreatingSideStory: false,
          notice: null,
          sideStories: upsertSideStory(state.sideStories, sideStory),
        }));
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    removeSideStoryImage: async (storyId, imagePath) => {
      const currentStory = get().sideStories.find(
        (story) => story.id === storyId,
      );
      if (!currentStory) {
        throw new StoryActionError(
          "not-found",
          "That Side Story is unavailable.",
        );
      }

      try {
        const updatedRecord = await deps.removeSideStoryImage(
          storyId,
          imagePath,
        );
        const sideStory = await resolveSideStoryView(
          updatedRecord,
          currentStory.attachments,
        );

        set((state) => ({
          activeSideStoryId: storyId,
          isCreatingSideStory: false,
          notice: null,
          sideStories: upsertSideStory(state.sideStories, sideStory),
        }));
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    deleteSideStory: async (storyId) => {
      const projectId = get().projectId;
      if (!projectId) {
        throw new StoryActionError(
          "not-found",
          "That Side Story is unavailable.",
        );
      }

      try {
        await deps.deleteSideStory(storyId);
        const capacity = await deps
          .getProjectStoryCapacity(projectId)
          .catch(() => get().capacity);

        set((state) => {
          const sideStories = state.sideStories.filter(
            (story) => story.id !== storyId,
          );

          return {
            activeSideStoryId: null,
            capacity,
            isCreatingSideStory: false,
            notice: null,
            sideStories,
          };
        });
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
        throw error;
      }
    },

    clearNotice: () => {
      set({ notice: null });
    },

    clearLimitPrompt: () => {
      set({ limitPrompt: null });
    },

    exportStoryPiece: async (input) => {
      return deps.exportStoryPiece(input);
    },

    refreshMediaUrls: async () => {
      const projectId = get().projectId;
      if (!projectId) {
        return;
      }

      set({
        isRecoveringMedia: true,
      });

      try {
        const workspace = await deps.loadStoryWorkspace(projectId);

        set((state) => ({
          isRecoveringMedia: false,
          mainStory: workspace.mainStory,
          notice: null,
          sideStories: workspace.sideStories,
          status: "ready",
          tier: workspace.tier,
          ...applySelection(workspace, state),
        }));
      } catch (error) {
        set({
          isRecoveringMedia: false,
          notice: {
            kind: "error",
            message: formatStoryErrorMessage(error),
          },
        });
      }
    },
  }));
}

export const storyWorkspaceStore = createStoryWorkspaceStore();

export function useStoryWorkspaceStore<T>(
  selector: (state: StoryWorkspaceStoreState) => T,
) {
  return useStore(storyWorkspaceStore, selector);
}

export function selectActiveChapter(
  state: Pick<StoryWorkspaceStoreState, "activeChapterId" | "mainStory">,
) {
  return (
    state.mainStory?.chapters.find(
      (chapter) => chapter.id === state.activeChapterId,
    ) ?? null
  );
}

export function selectActiveSideStory(
  state: Pick<StoryWorkspaceStoreState, "activeSideStoryId" | "sideStories">,
) {
  return (
    state.sideStories.find((story) => story.id === state.activeSideStoryId) ??
    null
  );
}

export function selectSideStoryDraft(
  state: Pick<StoryWorkspaceStoreState, "isCreatingSideStory">,
) {
  return state.isCreatingSideStory ? createSideStoryDraft() : null;
}

export function selectActiveContinuityIssue(
  state: Pick<
    StoryWorkspaceStoreState,
    "continuityIssues" | "selectedContinuityIssueId"
  >,
) {
  return (
    state.continuityIssues.find(
      (issue) => issue.id === state.selectedContinuityIssueId,
    ) ?? null
  );
}

export function selectActiveStructuralAnalysisRun(
  state: Pick<
    StoryWorkspaceStoreState,
    "selectedStructuralRunId" | "structuralRuns"
  >,
) {
  return (
    state.structuralRuns.find(
      (run) => run.id === state.selectedStructuralRunId,
    ) ?? null
  );
}

let structuralHistoryRunsCache:
  | StoryWorkspaceStoreState["structuralRuns"]
  | null = null;
let structuralHistoryEntriesCache = mapStructuralAnalysisHistoryEntries([]);

export function selectStructuralAnalysisHistory(
  state: Pick<StoryWorkspaceStoreState, "structuralRuns">,
) {
  if (state.structuralRuns === structuralHistoryRunsCache) {
    return structuralHistoryEntriesCache;
  }

  structuralHistoryRunsCache = state.structuralRuns;
  structuralHistoryEntriesCache = mapStructuralAnalysisHistoryEntries(
    state.structuralRuns,
  );
  return structuralHistoryEntriesCache;
}
